/**
 * Pressure → width multiplier. Linear blend between MIN_F and MAX_F.
 * pressure 0.5 (mouse / no-pressure devices) yields exactly baseWidth.
 */
const MIN_F = 0.55, MAX_F = 1.45;
export function pressureToWidth(pressure: number, baseWidth: number): number {
  const p = Math.min(1, Math.max(0, pressure));
  return baseWidth * (MIN_F + (MAX_F - MIN_F) * p);
}

/**
 * One-pass point thinning on a packed [x,y,pressure,dt] buffer.
 * Drops point B in a run A-B-C when B's perpendicular distance to segment AC
 * is < epsilon AND |pressureB - pressureA| < 0.08. dt of dropped points is
 * folded into the survivor so timing stays accurate for future features.
 */
export function thinPoints(points: Float32Array, epsilon: number): Float32Array {
  const n = points.length / 4;
  if (n <= 2) return points;
  const keep: number[] = [0];
  let anchor = 0; // index of last kept point
  for (let i = 1; i < n - 1; i++) {
    const ax = points[anchor * 4]!, ay = points[anchor * 4 + 1]!, ap = points[anchor * 4 + 2]!;
    const bx = points[i * 4]!, by = points[i * 4 + 1]!, bp = points[i * 4 + 2]!;
    const cx = points[(i + 1) * 4]!, cy = points[(i + 1) * 4 + 1]!;
    const d = perpDistance(bx, by, ax, ay, cx, cy);
    if (d >= epsilon || Math.abs(bp - ap) >= 0.08) {
      keep.push(i);
      anchor = i;
    }
  }
  keep.push(n - 1);
  if (keep.length === n) return points;
  const out = new Float32Array(keep.length * 4);
  let prevKept = 0, acc = 0, o = 0;
  for (let k = 0; k < keep.length; k++) {
    const idx = keep[k]!;
    // fold dt of skipped points into this one
    acc = 0;
    for (let j = prevKept + 1; j <= idx; j++) acc += points[j * 4 + 3]!;
    out[o++] = points[idx * 4]!;
    out[o++] = points[idx * 4 + 1]!;
    out[o++] = points[idx * 4 + 2]!;
    out[o++] = k === 0 ? points[3]! : acc;
    prevKept = idx;
  }
  return out;
}

function perpDistance(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - ax, py - ay);
  return Math.abs(dy * px - dx * py + bx * ay - by * ax) / Math.sqrt(len2);
}
